import PropTypes from 'prop-types';
import { employeeApi } from '@api/employeeApi';
import { Avatar, AvatarFallback, AvatarImage } from '@components/common/Avataar';
import { ScrollArea } from '@components/common/ScrollArea';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@components/common/Sheet';
import { ListSkeleton } from '@components/loaders/SkeletonLoaders';
import { cn } from '@helpers/styleUtils';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';

export const EmployeeInfo = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

    const handleSearchChange = useCallback(e => {
        setSearchTerm(e.target.value);
    }, []);

    const openSheet = useCallback(user => {
        setSelectedUser(user);
        setIsSheetOpen(true);
    }, []);

    const closeSheet = () => {
        setIsSheetOpen(false);
    };

    useEffect(() => {
        const fetchUserData = async () => {
            setIsLoading(true);
            try {
                const response = await employeeApi.getUserList();
                setUsers(response);
                setSelectedUser(response[0] || null);
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const filteredUsers = useMemo(
        () =>
            users.filter(user =>
                user.displayName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
            ),
        [users, debouncedSearchTerm],
    );

    return (
        <div className="col-span-1 bg-white shadow-md rounded-[10px] p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold mb-4">Employee Info</h1>
            </div>

            {isLoading ? (
                <ListSkeleton lines={3} />
            ) : (
                <>
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="border border-gray-300 rounded-sm p-2 w-full"
                        />
                    </div>

                    <ScrollArea className="h-[250px]">
                        <ul className="space-y-2">
                            {filteredUsers.map(user => (
                                <MemoizedUserItem
                                    user={user}
                                    onClick={() => openSheet(user)}
                                    key={user.id}
                                />
                            ))}
                        </ul>
                    </ScrollArea>

                    <Sheet open={isSheetOpen} onClose={closeSheet}>
                        <SheetContent className="sm:max-w-[30%]" onClose={closeSheet}>
                            <SheetHeader>
                                <SheetTitle>Employees info:</SheetTitle>
                                <SheetDescription></SheetDescription>
                            </SheetHeader>
                            {selectedUser && (
                                <div className="flex flex-col items-center text-center space-y-4 bg-gray-100 p-4 rounded-[10px]">
                                    <div className="w-full flex items-center justify-start gap-4">
                                        {/* Avatar */}
                                        <Avatar className="h-12 w-12">
                                            <AvatarImage src={selectedUser.photoUrl} />
                                            <AvatarFallback>
                                                {selectedUser.displayName.charAt(0)}
                                            </AvatarFallback>
                                        </Avatar>

                                        {/* User Info */}
                                        <div>
                                            <h2 className="text-base font-bold">
                                                {selectedUser.displayName}
                                            </h2>
                                            <p className="text-gray-500 text-md text-left">
                                                {selectedUser.jobTitle || 'Position Unavailable'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="w-full text-sm space-y-2 text-left">
                                        {selectedUser.mobilePhone && (
                                            <p>
                                                <span className="text-gray-500">Phone:</span>
                                                {selectedUser.mobilePhone}
                                            </p>
                                        )}
                                        {selectedUser.workEmail && (
                                            <p>
                                                <span className="text-gray-500">Email:</span>
                                                {selectedUser.workEmail}
                                            </p>
                                        )}
                                        {selectedUser.gender && (
                                            <p>
                                                <span className="text-gray-500">Gender:</span>
                                                {selectedUser.gender}
                                            </p>
                                        )}
                                        {selectedUser.location && (
                                            <p>
                                                <span className="text-gray-500">Location:</span>
                                                {selectedUser.location}
                                            </p>
                                        )}
                                        {selectedUser.dateOfBirth && (
                                            <p>
                                                <span className="text-gray-500">
                                                    Date of Birth:
                                                </span>
                                                {selectedUser.dateOfBirth}
                                            </p>
                                        )}
                                        {selectedUser.supervisor && (
                                            <p>
                                                <span className="text-gray-500">Supervisor:</span>
                                                {selectedUser.supervisor}
                                            </p>
                                        )}
                                        {selectedUser.department && (
                                            <p>
                                                <span className="text-gray-500">Department:</span>
                                                {selectedUser.department}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </SheetContent>
                    </Sheet>
                </>
            )}
        </div>
    );
};

const MemoizedUserItem = React.memo(({ user, onClick }) => (
    <li
        className={cn(
            'flex items-center justify-between p-2 border-b border-gray-200 cursor-pointer',
        )}
        onClick={onClick}
    >
        <div className="flex items-center gap-2 w-full">
            <Avatar className="h-8 w-8">
                <AvatarImage src={user.photoUrl} />
                <AvatarFallback>{user.displayName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
                <p className="text-md font-bold">{user.displayName}</p>
                <p className="text-xs text-gray-500">{user.jobTitle}</p>
            </div>
        </div>
        <MdOutlineKeyboardArrowRight />
    </li>
));

MemoizedUserItem.displayName = 'MemoizedUserItem';

MemoizedUserItem.propTypes = {
    user: PropTypes.shape({
        photoUrl: PropTypes.string,
        displayName: PropTypes.string.isRequired,
        jobTitle: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func.isRequired,
};
